import type { Language, Locale } from '@seek/melways-sites';
import type { IdentitySubject, LoginOptions } from '@seek/online-identity';

import { LANGUAGE } from 'src/app/constants/configObjectProperties';
import { LANGUAGE_LOCALE_DEFAULT } from 'src/app/constants/language';
import { getConfig } from 'src/app/utils/config';
import {
  isDev,
  isEnvMocked,
  isServerRendering,
} from 'src/app/utils/environment';

import { getAuthN } from './sso';

interface AuthService {
  getToken: (ignoreCache?: boolean) => Promise<string | null>;
  renewToken: () => Promise<string | null>;
  login: () => void;
  logout: () => void;
  getUserIdentity: () => Promise<IdentitySubject | undefined>;
  isAuthenticated: () => Promise<boolean>;
}

const createSsoAuthService = (): AuthService => {
  const authN = getAuthN();

  const login = async () => {
    const language: Language | Locale =
      getConfig(LANGUAGE) ?? LANGUAGE_LOCALE_DEFAULT;
    const options: LoginOptions = {
      additionalArguments: { language },
    };

    await authN.login(options);
    return null;
  };

  const logout = () => {
    window.location.href = getConfig('SSO_REDIRECT_URI');
  };

  const isAuthenticated = (): Promise<boolean> => authN.isAuthenticated();

  const getToken = async (ignoreCache = false): Promise<string | null> => {
    if (isDev() && isEnvMocked()) {
      return Promise.resolve('mockToken');
    }

    try {
      const isAuth = await isAuthenticated();

      if (!isAuth) {
        return login();
      }

      const token = await authN.getToken({ ignoreCache });
      if (typeof token === 'string') {
        return token;
      }
      return null;
    } catch (err: any) {
      if (err.error === 'login_required' || err.error === 'invalid_grant') {
        return login();
      }
      throw err;
    }
  };

  const getUserIdentity = async () => {
    const newUserIdentity = await authN.getIdentity();
    return newUserIdentity?.sub;
  };

  return {
    getToken,
    renewToken: () => getToken(true),
    login,
    logout,
    getUserIdentity,
    isAuthenticated,
  };
};

const createLocalAuthService = (): AuthService => {
  // NOTE: HIRER_GRAPHQL_API_AUTH_TOKEN can be specified as an env var
  // during development. It is populated from DefinePlugin
  const getToken = async () =>
    // @ts-ignore
    global.HIRER_GRAPHQL_API_AUTH_TOKEN ?? 'mockToken';
  const login = async () => {};
  const logout = async () => {};
  const getUserIdentity = async () => ({
    email: 'test@example.com',
    ID: '123',
  });
  const isAuthenticated = () => Promise.resolve(true);
  return {
    getToken,
    renewToken: getToken,
    login,
    logout,
    getUserIdentity,
    isAuthenticated,
  };
};

const createAuthService = (): AuthService => {
  const isLocalEnvMocked = isDev() && isEnvMocked();
  if (isServerRendering() || isLocalEnvMocked) {
    return createLocalAuthService();
  }
  return createSsoAuthService();
};

export { createAuthService };
